import api from '../utils/api';

/**
 * Get all identity types with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.pageSize - Items per page
 * @param {string} params.searchTerm - Search term
 * @param {boolean} params.isActive - Active status filter
 * @param {string} params.sortColumn - Column to sort by
 * @param {string} params.sortDirection - Sort direction (asc/desc)
 * @returns {Promise<Object>} Response with data and meta
 */
export const getAllIdentityTypes = async (params) => {
  // Pass params directly to API client (consistent with other services)
  const response = await api.get('/identity-types', params);
  return response;
};

/**
 * Get identity type by ID
 * @param {number} id - Identity type ID
 * @returns {Promise<Object>} Identity type data
 */
export const getIdentityTypeById = async (id) => {
  const response = await api.get(`/identity-types/${id}`);
  return response;
};

/**
 * Get initialization data for identity type form
 * @returns {Promise<Object>} Init data
 */
export const getIdentityTypeInit = async () => {
  const response = await api.get('/identity-types/init');
  return response;
};

/**
 * Create a new identity type
 * @param {Object} data - Identity type data
 * @param {string} data.code - Identity type code
 * @param {string} data.name - Identity type name
 * @param {string} data.description - Identity type description
 * @param {boolean} data.isActive - Active status
 * @param {number} data.displayOrder - Display order
 * @returns {Promise<Object>} Created identity type
 */
export const createIdentityType = async (data) => {
  const response = await api.post('/identity-types', data);
  return response;
};

/**
 * Update an existing identity type
 * @param {number} id - Identity type ID
 * @param {Object} data - Identity type data
 * @param {string} data.code - Identity type code
 * @param {string} data.name - Identity type name
 * @param {string} data.description - Identity type description
 * @param {boolean} data.isActive - Active status
 * @param {number} data.displayOrder - Display order
 * @returns {Promise<Object>} Updated identity type
 */
export const updateIdentityType = async (id, data) => {
  const response = await api.put(`/identity-types/${id}`, { id, ...data });
  return response;
};

/**
 * Delete an identity type
 * @param {number} id - Identity type ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteIdentityType = async (id) => {
  const response = await api.delete(`/identity-types/${id}`);
  return response;
};

/**
 * Bulk delete identity types
 * @param {Array<number>} ids - Array of identity type IDs
 * @returns {Promise<Object>} Deletion response
 */
export const bulkDeleteIdentityTypes = async (ids) => {
  const response = await api.delete('/identity-types', {
    params: { ids },
    paramsSerializer: {
      indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
    },
  });
  return response;
};
